import { isObject } from 'lodash';
import widgetSalesforceImageDT from '../../../../../assets/img/widget_salesforceDT.svg';
import { WIDGET_CONNECTION_CONFIG_FIELDS } from '../../../../common/consts';
import widgetGenericLogo from '../../../../../assets/img/widget_generic_logo.png';
import widgetReadyToSetup from '../../../../../assets/img/integration-logo.png';
import widgetReadyToSetupSalesforceImageDT from '../../../../../assets/img/widget_readyToSetupSalesforceDT.svg';

const configIdKey = 'id';
const configTitleKey = 'title';
const configDescKey = 'description';
const configTitleLogoKey = 'titleLogo';
const configDomainsKey = 'domains';
const configIntegrationKey = 'integration';

const defaultDomains = {
  z0: {},
  z1: {},
  z2: {},
  z3: {},
  'z1-a': {},
};

const WIDGET_ID_KEYS = Object.freeze({
  SALESFORCE: 'SALESFORCE',
});

const DEFAULT_CONFIGURATION_STEPS = [
  {
    name: 'get-started-step',
    component: 'WidgetGetStarted',
    params: {
      illustration: widgetReadyToSetup,
      learn_more: 'https://knowledge.liveperson.com',
    },
  },
  {
    name: 'setup-step',
    component: 'WidgetSetupStep',
    navigator: {
      title: 'Setup',
      subtitle: 'Setup your connection',
    },
    footer: {
      next: 'Next',
    },
  },
  {
    name: 'customize-step',
    component: 'WidgetCustomizeStep',
    navigator: {
      title: 'Customize',
      subtitle: 'Add widget name and other details',
    },
    footer: {
      back: 'Back',
      next: 'Next',
    },
  },
  {
    name: 'map-responses-step',
    component: 'WidgetMapResponsesStep',
    navigator: {
      title: 'Configure',
      subtitle: 'Map responses and sync transcript',
    },
    footer: {
      back: 'Back',
      next: 'Next',
    },
  },
  {
    name: 'launch-step',
    component: 'WidgetLaunchStep',
    navigator: {
      title: 'Launch',
      subtitle: 'Preview and launch your widget in Agent Workspace',
    },
    footer: {
      back: 'Back',
      launch: 'Launch Now',
    },
  },
];

const SALESFORCE_WIDGET_CONFIGURATION_STEPS = [
  {
    name: 'get-started-step',
    component: 'WidgetGetStarted',
    params: {
      illustration: widgetReadyToSetupSalesforceImageDT,
      learn_more: 'https://knowledge.liveperson.com/salesforce-connector-ea',
    },
  },
  {
    name: 'setup-step',
    component: 'WidgetSetupStep',
    navigator: {
      title: 'Setup',
      subtitle: 'Setup your Salesforce connection',
    },
    footer: {
      delete: 'Reset',
      test: 'Save & Test',
      next: 'Next',
    },
  },
  {
    name: 'customize-step',
    component: 'WidgetCustomizeStep',
    navigator: {
      title: 'Customize',
      subtitle: 'Add widget name and other details',
    },
    footer: {
      back: 'Back',
      next: 'Next',
    },
  },
  {
    name: 'map-responses-step',
    component: 'WidgetMapResponsesStep',
    navigator: {
      title: 'Configure',
      subtitle: 'Map responses and sync transcript',
    },
    footer: {
      back: 'Back',
      next: 'Next',
    },
  },
  {
    name: 'launch-step',
    component: 'WidgetLaunchStep',
    navigator: {
      title: 'Launch',
      subtitle: 'Preview and launch your widget in Agent Workspace',
    },
    footer: {
      back: 'Back',
      launch: 'Launch Now',
    },
  },
];

/**
 * STATIC WIDGETS CONFIGURATIONS/DEFINITIONS FOR THE WIZARD/SETUP STEP.
 *
 * Below are the widget configurations. Please note that the `id` (`configIdKey`) is required.
 * Remaining parameters are optional, but it is recommended that all widgets have their title,
 * description, icon etc.
 */

const SALESFORCE_WIDGET_CONFIG = Object.freeze({
  [configIdKey]: WIDGET_ID_KEYS.SALESFORCE,
  [configTitleKey]: 'Salesforce Widget',
  [configDescKey]: 'Empower agents with quick and easy access to customer case information.',
  [configTitleLogoKey]: widgetSalesforceImageDT,
  [configIntegrationKey]: WIDGET_CONNECTION_CONFIG_FIELDS.INTEGRATION_NAME_SALESFORCE,
});

/**
 * STATIC CONFIGURATIONS LISTED (TO BE EXPOSED).
 *
 * Below is the array of the configurations to be exposed to the client.
 */

const WIDGETS_CONFIGURATION_RAW = Object.freeze([
  SALESFORCE_WIDGET_CONFIG,
]);

/**
 * Ensures that all required fields are available in the configuration. If not, provides the defaults.
 *
 * @param config current widget configuration context.
 * @returns {object|null} frozen widget configuration with default values, if relevant.
 */
const defaultize = (config) => {
  const defaultized = isObject(config) && config[configIdKey] ? {
    ...config,
    [configTitleKey]: config[configTitleKey] ? config[configTitleKey] : 'Widget',
    [configDescKey]: config[configDescKey] ? config[configDescKey] : 'New widget',
    [configTitleLogoKey]: config[configTitleLogoKey] ? config[configTitleLogoKey] : widgetGenericLogo,
    [configDomainsKey]: config[configDomainsKey] ? config[configDomainsKey] : { ...defaultDomains },
    [configIntegrationKey]: config[configIntegrationKey] ? config[configIntegrationKey] : null,
  } : null;
  return Object.freeze(defaultized);
};

const WIDGETS_CONFIGURATION = Object.freeze(WIDGETS_CONFIGURATION_RAW.map(defaultize));

const CONNECTION_TEST_RESULT_STATE = {
  NONE: 'none',
  SUCCESS: 'success',
  FAILURE: 'failure',
};

export {
  WIDGET_ID_KEYS,
  WIDGETS_CONFIGURATION,
  DEFAULT_CONFIGURATION_STEPS,
  SALESFORCE_WIDGET_CONFIGURATION_STEPS,
  CONNECTION_TEST_RESULT_STATE,
};
